import { QuestionType } from './QuestionType';
import { BlockNote } from './BlockNote';

export type ExerciseSeriesType = 'exercises';
export type ExerciseLevel = '1' | '2' | '3';
export enum ExerciseType {
  H5P = 'h5p',
  BOOK_WIDGET = 'book_widget',
  ALGEBRA_KIT = 'algebra_kit',
  TEXT_EXERCISE = 'text_exercise',
}
export type ExerciseTool = 'fully_digital' | 'digital_with_paper' | null | string;

export enum BloomType {
  ANALYSIS = 'analysis',
  COMPREHEND = 'comprehend',
  CREATE = 'create',
  EVALUATE = 'evaluate',
  REMEMBER = 'remember',
  APPLICATION = 'application',
}

type BackendExerciseSeriesInfo = {
  id: string;
  title: string;
  type: ExerciseSeriesType;
  blocks_of_level: [];
};

export type ExerciseSeriesInfo = Omit<BackendExerciseSeriesInfo, 'blocks_of_level'> & {
  blocksOfLevel: [];
};

type BackendExerciseSeries = BackendExerciseSeriesInfo & {
  blocks: [];
};

export type ExerciseSeries = ExerciseSeriesInfo & {
  blocks: [];
};

export type ClientSettings = {
  width: number;
  height: number;
  Width?: number;
};

export type Exercise = {
  id: string;
  title: string;
  description?: string;
  level: ExerciseLevel;
  bloom_type: BloomType;
  type: ExerciseType;
  question_type?: QuestionType;
  url?: string;
  client_settings?: ClientSettings;
  algebra_kit_exercise_id?: string;
  parent_id?: string | undefined;
  note?: BlockNote | undefined;
  page_type?: string | undefined;
  position?: number;
  exercise_tool: ExerciseTool;
  content?: string;
  is_printable?: boolean;
  studentLatestScore?: {
    score: number;
    maxScore: number;
  } | null;
};

export type ExerciseWithNotes = Exercise & {
  note?: BlockNote | undefined;
  pageType?: string | undefined;
};

export type ExerciseDuplicate = {
  newId: string;
  originalUrl: string;
};

// BACKEND

export type BackendExercise = Omit<Exercise, 'clientSettings' | 'bloomType' | 'questionType'> & {
  bloom_type: BloomType;
  client_settings: ClientSettings;
  question_type: QuestionType;
  algebra_kit_exercise_id: string;
  parent_id?: string | undefined;
  exercise_tool: ExerciseTool;
  studentLatestScore?: { score: number; maxScore: number };
};

// API

export type GetSeriesResponse = {
  series: ExerciseSeries[];
};

const parseQuestionType = (input?: string): QuestionType | undefined => {
  return input ? (input.split('-')[0] as QuestionType) : undefined;
};

// MAPPERS
export const getSeriesMapper = (response: any): ExerciseSeries[] =>
  response.data.map(
    (series: BackendExerciseSeries): ExerciseSeries => ({
      id: series.id,
      title: series.title,
      type: series.type,
      blocksOfLevel: series.blocks_of_level,
      blocks: series.blocks,
    })
  );

const mapBackendExerciseToExercise = (data: BackendExercise): Exercise => ({
  id: data.id,
  title: data.title,
  description: data.description,
  level: data.level.toString() as ExerciseLevel,
  type: data.type,
  question_type: parseQuestionType(data.question_type),
  bloom_type: data.bloom_type,
  url: data.url,
  client_settings: data.client_settings,
  algebra_kit_exercise_id: data.algebra_kit_exercise_id,
  parent_id: data.parent_id,
  exercise_tool: data.exercise_tool,
  studentLatestScore: data?.studentLatestScore
    ? {
        score: data?.studentLatestScore?.score,
        maxScore: data?.studentLatestScore?.maxScore,
      }
    : null,
});

export const getExercisesMapper = (response: any): Exercise[] =>
  response.data.map(mapBackendExerciseToExercise);

export const getExerciseMapper = (response: { data: BackendExercise }): Exercise =>
  mapBackendExerciseToExercise(response.data);
